import React, { useEffect, useId, useRef } from 'react';
import { shotsCsv } from '../game/model.mjs';

export function Button({ children, primary = false, subtle = false, className = '', ...props }) {
  return <button type="button" className={`button ${primary ? 'primary' : ''} ${subtle ? 'subtle' : ''} ${className}`} {...props}>{children}</button>;
}
export function Options({ label, value, options, onChange, disabled = false }) {
  return <div className="options" role="group" aria-label={label}>{options.map(option => <Button key={option.value} aria-pressed={value === option.value} disabled={disabled} onClick={() => onChange(option.value)}>{option.label}</Button>)}</div>;
}
export function Notice({ children, error = false, actions }) {
  return <div className={`notice ${error ? 'error-notice' : ''}`} role={error ? 'alert' : 'status'}><p>{children}</p>{actions && <div className="inline-actions">{actions}</div>}</div>;
}
export function PageHeading({ title, subtitle, children }) {
  return <div className="page-heading"><div><h1 tabIndex="-1">{title}</h1>{subtitle && <p className="muted">{subtitle}</p>}</div>{children && <div className="heading-actions">{children}</div>}</div>;
}
export function Modal({ title, children, onClose, footer, wide = false }) {
  const dialogRef = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = dialogRef.current;
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
    dialog.querySelector('button')?.focus();
    return () => { dialog.close(); document.body.style.overflow = previousOverflow; if (opener?.isConnected) opener.focus({ preventScroll: true }); };
  }, []);
  function trapFocus(event) {
    if (event.key !== 'Tab') return;
    const elements = [...dialogRef.current.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex="0"]')].filter(element => element.getClientRects().length > 0);
    const first = elements[0], last = elements[elements.length - 1];
    if (event.shiftKey && (document.activeElement === first || !dialogRef.current.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
  }
  function backdropClick(event) {
    if (event.target !== dialogRef.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose();
  }
  return <dialog ref={dialogRef} aria-labelledby={titleId} className={`modal ${wide ? 'wide' : ''}`} onKeyDown={trapFocus} onCancel={event => { event.preventDefault(); onClose(); }} onClick={backdropClick}>
    <div className="modal-layout"><header className="modal-header"><h2 id={titleId}>{title}</h2><Button subtle onClick={onClose} aria-label={`Close ${title}`} autoFocus>×</Button></header>
      <div className="modal-body">{children}</div><footer className="modal-footer">{footer || <Button onClick={onClose}>Close</Button>}</footer>
    </div>
  </dialog>;
}
export function DownloadButton({ game, csv = false }) {
  function download() {
    const text = csv ? shotsCsv(game) : JSON.stringify(game, null, 2);
    const url = URL.createObjectURL(new Blob([text], { type: csv ? 'text/csv;charset=utf-8' : 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `battleship-${game.game_id}.${csv ? 'csv' : 'json'}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return <Button subtle onClick={download}>Download {csv ? 'shots CSV' : 'match JSON'}</Button>;
}
